// Libs
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  SetSharedKitProducts,
  UpdateSharedKitItems,
} from '../../../actions/sharedKits';

// Hooks
import useSharedKitActions from './useSharedKitActions';

const useSharedKitProducts = () => {
  const dispatch = useDispatch();
  const currentKit = useSelector((state) => state.sharedKits?.currentKit);
  const { onDeleteClick } = useSharedKitActions(currentKit);

  const saveProducts = useCallback(
    (products) => {
      dispatch(SetSharedKitProducts(products));
      dispatch(UpdateSharedKitItems({ kitId: currentKit.id, products }));
    },
    [dispatch, currentKit],
  );

  const getProductQty = useCallback(
    (productId) =>
      currentKit?.products?.find((item) => item.product === productId)?.nums ??
      null,
    [currentKit],
  );

  const setCount = useCallback(
    (count, productId) => {
      if (!currentKit) {
        return;
      }

      if (count <= 0) {
        if (currentKit.products.length === 1) {
          onDeleteClick(currentKit.id);
          return;
        }
        saveProducts(
          currentKit.products.filter((item) => item.product !== productId),
        );
        return;
      }

      saveProducts(
        currentKit.products.map((item) =>
          item.product === productId ? { ...item, nums: count } : item,
        ),
      );
    },
    [currentKit, saveProducts, onDeleteClick],
  );

  const addOne = useCallback(
    (productId) => setCount((getProductQty(productId) || 0) + 1, productId),
    [setCount, getProductQty],
  );

  const removeOne = useCallback(
    (productId) => setCount((getProductQty(productId) || 0) - 1, productId),
    [setCount, getProductQty],
  );

  const removeProduct = useCallback(
    (productId) => setCount(0, productId),
    [setCount],
  );

  const addProducts = useCallback(
    (newProducts) => {
      if (!currentKit || !newProducts.length) {
        return;
      }

      const existing = new Map(
        currentKit.products.map((item) => [item.product, item]),
      );
      newProducts.forEach(({ productId, nums }) => {
        existing.set(productId, { product: productId, nums });
      });

      saveProducts([...existing.values()]);
    },
    [currentKit, saveProducts],
  );

  return {
    currentKit,
    getProductQty,
    setCount,
    addOne,
    removeOne,
    removeProduct,
    addProducts,
  };
};

export default useSharedKitProducts;
